import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { DEEP_GREEN, MUTED, WARN_ORANGE } from '../colorTokens';
import { KIND_LABEL, formatDate, formatDays, formatNumber, formatPercent } from './format';
import { Caveat, Headline, MeasureRow, ScoreCard, TrendLine, versionRange } from './ScoreParts';

const DAY_MS = 86400000;
const KIND_PLURAL = { feature: 'feature rollouts', maintenance: 'maintenance rollouts', hotfix: 'hotfix rollouts' };
const KIND_COLOR = { feature: DEEP_GREEN, maintenance: '#6f8f7d', hotfix: '#D74B00' };

const Stat = ({ label, value, hint }) => (
  <Tooltip title={hint || ''} placement="top" disableHoverListener={!hint}>
    <Box sx={{ pr: 3, mr: 3, borderRight: '1px solid rgba(0,0,0,0.08)', '&:last-of-type': { border: 0, mr: 0, pr: 0 } }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: DEEP_GREEN, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: MUTED }}>{label}</Typography>
    </Box>
  </Tooltip>
);

const KindChip = ({ kind }) => (
  <Chip
    size="small"
    label={KIND_LABEL[kind]}
    sx={{ height: 22, borderRadius: '8px', fontWeight: 500, bgcolor: kind === 'hotfix' ? 'rgba(215,75,0,0.12)' : 'rgba(26,46,34,0.08)', color: kind === 'hotfix' ? '#B33F00' : DEEP_GREEN }}
  />
);

const monthTicks = (min, max) => {
  const ticks = [];
  const d = new Date(min);
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  while (d.getTime() <= max) {
    if (d.getTime() >= min) ticks.push({ at: d.getTime(), label: d.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' }) });
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return ticks;
};

const Timeline = ({ rows }) => {
  const span = useMemo(() => {
    if (!rows.length) return null;
    const min = Math.min(...rows.map((r) => new Date(r.rolloutStart).getTime())) - DAY_MS;
    const max = Math.max(...rows.map((r) => new Date(r.rolloutEnd).getTime())) + DAY_MS;
    return { min, max, total: max - min, ticks: monthTicks(min, max) };
  }, [rows]);
  if (!span) return null;
  const pct = (t) => `${((t - span.min) / span.total) * 100}%`;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>Rollout timeline</Typography>
        {Object.keys(KIND_COLOR).map((k) => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: KIND_COLOR[k] }} />
            <Typography variant="caption" sx={{ color: MUTED }}>{KIND_LABEL[k]}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: '6px', alignItems: 'center' }}>
        <Box />
        <Box sx={{ position: 'relative', height: 18 }}>
          {span.ticks.map((t) => (
            <Typography key={t.at} variant="caption" sx={{ position: 'absolute', left: pct(t.at), color: MUTED, transform: 'translateX(-2px)' }}>{t.label}</Typography>
          ))}
        </Box>
        {rows.map((r) => {
          const left = new Date(r.rolloutStart).getTime();
          const width = Math.max(new Date(r.rolloutEnd).getTime() - left, DAY_MS * 0.6);
          return (
            <React.Fragment key={r.version}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: DEEP_GREEN }}>{r.version}</Typography>
              <Box sx={{ position: 'relative', height: 22, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '6px' }}>
                {span.ticks.map((t) => (
                  <Box key={t.at} sx={{ position: 'absolute', left: pct(t.at), top: 0, bottom: 0, borderLeft: '1px dashed rgba(0,0,0,0.08)' }} />
                ))}
                <Tooltip
                  placement="top"
                  title={`${r.version} · ${KIND_LABEL[r.kind]} · ${formatDate(r.rolloutStart)} – ${formatDate(r.rolloutEnd)} · ${formatNumber(r.instances)} instances · failure rate ${formatPercent(r.failureRate)}${r.startsAtHistoryEdge ? ' · started before recorded history' : ''}`}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: pct(left),
                      width: `${(width / span.total) * 100}%`,
                      top: 3,
                      bottom: 3,
                      borderRadius: '5px',
                      bgcolor: KIND_COLOR[r.kind],
                      transition: 'filter 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': { filter: 'brightness(1.15)' }
                    }}
                  />
                </Tooltip>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

const trendTip = (p) => ({
  title: p.version,
  kind: KIND_LABEL[p.kind],
  lines: [`${formatPercent(p.failureRate)} of runs failed`, `${formatNumber(p.instances)} instances · ${formatNumber(p.rollbacks)} rollbacks${p.partial ? ' · started before recorded history' : ''}`]
});

const RolloutScorecard = ({ scorecard }) => {
  if (!scorecard || !scorecard.latest) return null;
  const { latest, baseline, measures, trend } = scorecard;
  const failure = measures.find((m) => m.key === 'failureRate');
  const since = baseline && `since the first ${baseline.versions.length > 1 ? `${baseline.versions.length} ` : ''}${KIND_PLURAL[baseline.kind]} (${versionRange(baseline.versions)}), which averaged ${formatPercent(failure.earlier)}.`;
  return (
    <ScoreCard
      headline={<Headline caption={`Latest ${KIND_LABEL[latest.kind].toLowerCase()} rollout · ${latest.version}`} value={formatPercent(failure.value)} label="of deployment runs failed" measure={failure} since={since} />}
      chart={<TrendLine title="Failure rate, rollout by rollout" legend="Filled dot: maintenance or hotfix · ring: feature release" data={trend} valueKey="failureRate" unit="rate" axis="zero" tip={trendTip} />}
    >
      <MeasureRow measures={measures} skip={['failureRate']} baselineLabel={baseline && `average of ${versionRange(baseline.versions)}`} />
      {!baseline && <Caveat>Improvement appears once a second {KIND_LABEL[latest.kind].toLowerCase()} rollout is recorded.</Caveat>}
      {baseline && (baseline.partial || latest.partial) && (
        <Caveat>Part of this comparison began before the recorded history, so its earlier figures may be understated and the improvement smaller than shown.</Caveat>
      )}
    </ScoreCard>
  );
};

const CadenceView = ({ overview }) => {
  const [showPrerelease, setShowPrerelease] = useState(false);
  const rows = useMemo(() => overview.releases.filter((r) => showPrerelease || !r.prerelease), [overview, showPrerelease]);
  const { kpis } = overview;

  if (!overview.releases.length) {
    return (
      <Typography sx={{ color: MUTED, mt: 3 }}>
        No deployment runs are recorded yet. Use "Sync from Datadog" to load the last 30 days; the daily sync then keeps the history.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <RolloutScorecard scorecard={overview.scorecard} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 2 }}>
        <Stat label="Releases rolled out" value={kpis.releases} />
        <Stat label="Started in last 90 days" value={kpis.releasesLast90Days} />
        <Stat label="Median gap between releases" value={formatDays(kpis.medianGapDays)} hint="Days between the first rollout events of consecutive versions." />
        <Stat label="Median rollout length" value={formatDays(kpis.medianRolloutDays)} hint="First to last instance event of a version." />
        <Stat label="Hotfix share" value={formatPercent(kpis.hotfixShare, 0)} hint="Four-part versions, or a maintenance release starting within 14 days of its feature release." />
        <Stat label="Run failure rate" value={formatPercent(kpis.failureRate)} hint="Failed, verification-failed, timelapse and rollback runs over success + failed. Skipped runs are excluded." />
        <Stat label="Rollbacks" value={formatNumber(kpis.rollbacks)} />
      </Box>

      <FormControlLabel
        sx={{ m: 0, color: MUTED }}
        control={<Switch size="small" checked={showPrerelease} onChange={(e) => setShowPrerelease(e.target.checked)} />}
        label={<Typography variant="body2">Include pre-release builds (alpha / RC)</Typography>}
      />

      <Timeline rows={rows} />

      <Paper variant="outlined" sx={{ borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FA', '& th': { color: DEEP_GREEN, fontWeight: 600, borderBottom: '2px solid rgba(26,46,34,0.15)' } }}>
              <TableCell>Version</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Rollout start</TableCell>
              <TableCell>Rollout end</TableCell>
              <TableCell align="right">Days</TableCell>
              <TableCell align="right">Since previous</TableCell>
              <TableCell align="right">Instances</TableCell>
              <TableCell align="right">Failed runs</TableCell>
              <TableCell align="right">Failure rate</TableCell>
              <TableCell align="right">Recovered</TableCell>
              <TableCell align="right">Still failing</TableCell>
              <TableCell align="right">Rollbacks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...rows].reverse().map((r) => (
              <TableRow key={r.version} hover sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(0,0,0,0.01)' } }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {r.version}
                  {r.smallRollout && (
                    <Tooltip title="Fewer than 10 instances ran this version, so it is left out of the gap and KPI figures.">
                      <Chip size="small" label="Small" sx={{ ml: 1, height: 20, borderRadius: '8px', fontWeight: 500 }} />
                    </Tooltip>
                  )}
                  {r.startsAtHistoryEdge && (
                    <Tooltip title="Rollout began at or before the start of the recorded history, so the start date, length and gaps are understated.">
                      <Chip size="small" label="Partial" sx={{ ml: 1, height: 20, borderRadius: '8px', fontWeight: 500 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell><KindChip kind={r.kind} /></TableCell>
                <TableCell>{formatDate(r.rolloutStart)}</TableCell>
                <TableCell>{formatDate(r.rolloutEnd)}</TableCell>
                <TableCell align="right">{r.rolloutDays ?? '–'}</TableCell>
                <TableCell align="right">{formatDays(r.gapDays)}</TableCell>
                <TableCell align="right">{formatNumber(r.instances)}</TableCell>
                <TableCell align="right">{formatNumber(r.failed)}</TableCell>
                <TableCell align="right" sx={{ color: r.failureRate > 0.05 ? WARN_ORANGE : 'inherit', fontWeight: r.failureRate > 0.05 ? 600 : 400 }}>
                  {formatPercent(r.failureRate)}
                </TableCell>
                <TableCell align="right">{formatNumber(r.instancesRecovered)}</TableCell>
                <TableCell align="right">{formatNumber(r.instancesStillFailing)}</TableCell>
                <TableCell align="right">{formatNumber(r.rollbacks)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default CadenceView;
